import type { ExperienceItem, TrackExperienceMetadata } from '@/types/abtest'
import { pushConvertActionToDatalayer, pushConvertExperimentsToDataLayer } from '@/utils/abtest'

export type TrackExperienceOptions = {
  experienceKey: string
} & TrackExperienceMetadata

export type TrackExperienceInput = string | TrackExperienceOptions

export type TrackOptions = {
  experiences?: TrackExperienceInput[]
}

function normalizeTrackExperienceInput(entry: TrackExperienceInput): TrackExperienceOptions {
  return typeof entry === 'string' ? { experienceKey: entry } : entry
}

function trackExperienceOptionsToMetadata(entry: TrackExperienceOptions): TrackExperienceMetadata {
  const { experienceKey: _k, ...rest } = entry
  return {
    ...rest
  }
}

export const useConvert = () => {
  const route = useRoute()
  const authStore = useAuthStore()
  const variationsCookie = useCookie<ExperienceItem[]>('ota-conv-ab')
  const visitorIdCookie = useCookie('ota-conv-context')

  const pushCurrentVisitorExperimentToDataLayer = (
    experienceKey: string,
    metadata?: TrackExperienceMetadata
  ) => {
    const experiences = variationsCookie.value || []
    const visitorId = visitorIdCookie.value || ''
    const userId = authStore.authId

    if (!visitorId) {
      return
    }

    const matchingExperience = experiences.find((experience) => experience?.experienceKey === experienceKey)

    if (!matchingExperience) {
      return
    }

    pushConvertExperimentsToDataLayer(
      [matchingExperience],
      visitorId,
      userId,
      metadata?.memberId
    )
  }

  const pushCurrentVisitorExperimentInteractionToDataLayer = (
    experienceKey: string,
    interaction: string,
    metadata?: TrackExperienceMetadata
  ) => {
    const experiences = variationsCookie.value || []
    const visitorId = visitorIdCookie.value || ''
    const userId = authStore.authId

    if (!visitorId) {
      return
    }

    const matchingExperience = experiences.find((experience) => experience?.experienceKey === experienceKey)

    if (!matchingExperience) {
      return
    }

    pushConvertActionToDatalayer(interaction, matchingExperience, visitorId, userId, metadata?.memberId)
  }

  const track = async (options: TrackOptions = {}): Promise<ExperienceItem[] | null> => {
    const experienceEntries: TrackExperienceOptions[] = (options.experiences ?? []).map(
      normalizeTrackExperienceInput
    )

    const experienceKeys = experienceEntries.map((e) => e.experienceKey)

    if (!experienceKeys.length) {
      return null
    }

    const metaByExperienceKey = new Map<string, TrackExperienceOptions>()
    for (const entry of experienceEntries) {
      metaByExperienceKey.set(entry.experienceKey, entry)
    }

    const variations = variationsCookie.value || []
    const matchedVariations = variations.filter((v) => experienceKeys.includes(v.experienceKey))

    if (!matchedVariations.length) {
      return null
    }

    await Promise.all(
      matchedVariations.map((variation) =>
        $fetch('/nuxt-api/convert/track', {
          method: 'POST',
          body: { variation }
        }).catch((error) => {
          logError(error)
        })
      )
    )

    for (const { experienceKey } of matchedVariations) {
      const entry = metaByExperienceKey.get(experienceKey)
      pushCurrentVisitorExperimentToDataLayer(
        experienceKey,
        entry ? trackExperienceOptionsToMetadata(entry) : undefined
      )
    }

    return matchedVariations
  }

  const refreshConvertVariations = async () => {
    try {
      await $fetch('/nuxt-api/convert/refresh', {
        method: 'POST',
        body: {
          pathname: route.path
        }
      })
    } catch (error) {
      logError(error)
    }
  }

  return {
    track,
    refreshConvertVariations,
    pushCurrentVisitorExperimentToDataLayer,
    pushCurrentVisitorExperimentInteractionToDataLayer
  }
}
